import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { SimulationProgress, SimulationResults, TaskStatus, SimulationTaskStatus } from '../../../types/simulation';
import { AppError } from '../../../types/error.d'; // Import AppError
import { Icon } from '@liftango/ui-icons';

interface SimulationProgressModalProps {
  open: boolean;
  onComplete: (results: SimulationResults) => void;
  onCancel: () => void;
  onError?: (error: AppError) => void; 
  appError?: AppError; 
  progressUpdate?: TaskStatus | null;
}

const SimulationProgressModal: React.FC<SimulationProgressModalProps> = ({
  open,
  onComplete,
  onCancel,
  onError,
  appError,
  progressUpdate,
}) => {
  const { t } = useTranslation(['mapDialogs', 'common']);
  const [progress, setProgress] = useState<SimulationProgress | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);

  const normalizeStatus = (statusValue: unknown): SimulationTaskStatus => {
    const normalized = String(statusValue ?? 'pending').toLowerCase();
    if (normalized === 'running' || normalized === 'completed' || normalized === 'failed') {
      return normalized;
    }
    return 'pending';
  };

  useEffect(() => {
    if (open) {
      // Reset state when modal opens
      setProgress({
        taskId: 'unknown',
        status: 'pending',
        message: t('mapDialogs:simProgress.initializing')
      });
      setIsCancelled(false);
    }
  }, [open]);

  useEffect(() => {
    if (progressUpdate) {
      const normalizedStatus = normalizeStatus(progressUpdate.status);
      setProgress({
        taskId: progressUpdate.task_id || 'unknown',
        status: normalizedStatus,
        message: progressUpdate.message || '',
        progress: progressUpdate.progress,
        results: progressUpdate.summary ? {
          results_id: progressUpdate.results_id || 'unknown',
          scenario_id: 'unknown',
          status: normalizedStatus === 'completed' ? 'completed' : 'failed',
          summary: {
            assigned_rides: progressUpdate.summary.assigned_rides,
            unassigned_rides: progressUpdate.summary.unassigned_rides,
            total_rides: (progressUpdate.summary.assigned_rides || 0) + (progressUpdate.summary.unassigned_rides || 0),
            total_vehicles: 0, // Not available in TaskStatus
            total_distance: 0, // Not available in TaskStatus
            total_cost: 0, // Not available in TaskStatus
            average_wait_time: 0 // Not available in TaskStatus
          }
        } : undefined
      });
      // The onError callback is still expecting a string, so we pass the main message from appError if present.
      if (normalizedStatus === 'failed' && progressUpdate.message && onError) {
        // Create an AppError from the progressUpdate message
        const errorToPass = new AppError({
          what: t('mapDialogs:simProgress.errorProgressTitle'),
          why: progressUpdate.message,
          howToFix: t('mapDialogs:simProgress.errorProgressHowToFix'),
          category: 'BusinessLogic',
          details: progressUpdate
        });
        onError(errorToPass);
      }
    }
  }, [progressUpdate, onError]);

  const handleCancel = () => {
    setIsCancelled(true);
    onCancel();
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <Icon size={20} name="circle-check" />;
      case 'failed':
        return <Icon size={20} name="circle-exclamation" />;
      case 'running':
        return <CircularProgress size={20} />;
      default:
        return <Icon size={20} name="hourglass-half" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusMessage = (status: string) => {
    if (appError) {
      return appError.structuredError.what;
    }
    switch (status) {
      case 'pending':
        return t('mapDialogs:simProgress.waiting');
      case 'running':
        return t('mapDialogs:simProgress.running');
      case 'completed':
        return t('mapDialogs:simProgress.completed');
      case 'failed':
        return t('mapDialogs:simProgress.failed');
      default:
        return t('mapDialogs:simProgress.unknown');
    }
  };

  const renderProgressContent = () => {
    if (!progress && !appError) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t('mapDialogs:simProgress.initializingShort')}</Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {/* Error Display */}
        {appError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">
              {appError.structuredError.what}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('mapDialogs:simProgress.whyLabel', { value: appError.structuredError.why })}
            </Typography>
            {appError.structuredError.howToFix && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('mapDialogs:simProgress.howToFixLabel', { value: appError.structuredError.howToFix })}
              </Typography>
            )}
          </Alert>
        )}

        {/* Status Header */}
        {!appError && progress && ( // Only show progress if no critical appError
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {getStatusIcon(progress.status)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              {getStatusMessage(progress.status)}
            </Typography>
            <Chip
              label={progress.status.toUpperCase()}
              color={getStatusColor(progress.status) as any}
              size="small"
              sx={{ ml: 'auto' }}
            />
          </Box>
        )}

        {/* Progress Bar */}
        {!appError && progress && progress.status === 'running' && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant={progress.progress !== undefined ? 'determinate' : 'indeterminate'}
              value={progress.progress || 0}
              sx={{ height: 8, borderRadius: 4 }}
            />
            {progress.progress !== undefined && (
              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
                {t('mapDialogs:simProgress.percentComplete', { value: Math.round(progress.progress) })}
              </Typography>
            )}
          </Box>
        )}

        {/* Task ID */}
        {!appError && progress && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('mapDialogs:simProgress.taskId', { value: progress.taskId })}
          </Typography>
        )}

        {/* Message */}
        {!appError && progress?.message && (
          <Alert severity={progress.status === 'failed' ? 'error' : 'info'} sx={{ mb: 2 }}>
            {progress.message}
          </Alert>
        )}

        {/* Results Summary */}
        {!appError && progress?.results && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {t('mapDialogs:simProgress.resultsTitle')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
              <Typography variant="body2">
                {t('mapDialogs:simProgress.assignedRides', { value: progress.results.summary.assigned_rides })}
              </Typography>
              <Typography variant="body2">
                {t('mapDialogs:simProgress.unassignedRides', { value: progress.results.summary.unassigned_rides })}
              </Typography>
              <Typography variant="body2">
                {t('mapDialogs:simProgress.totalRides', { value: progress.results.summary.total_rides })}
              </Typography>
              <Typography variant="body2">
                {t('mapDialogs:simProgress.successRate', { value: progress.results.summary.total_rides > 0
                  ? Math.round((progress.results.summary.assigned_rides / progress.results.summary.total_rides) * 100)
                  : 0 })}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('mapDialogs:simProgress.title')}
      </DialogTitle>
      <DialogContent>
        {appError && ( // Always show appError if present
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="h6">
              {appError.structuredError.what}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {t('mapDialogs:simProgress.whyLabel', { value: appError.structuredError.why })}
            </Typography>
            {appError.structuredError.howToFix && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {t('mapDialogs:simProgress.howToFixLabel', { value: appError.structuredError.howToFix })}
              </Typography>
            )}
          </Alert>
        )}
        {(!appError || (appError && progress)) && renderProgressContent()} 
      </DialogContent>
      <DialogActions>
        {progress?.status === 'running' && !appError && (
          <Button onClick={handleCancel} color="warning">
            {t('common:cancel')}
          </Button>
        )}
        {(progress?.status === 'completed' || progress?.status === 'failed' || appError) && (
          <Button onClick={handleCancel} variant="contained">
            {t('common:close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SimulationProgressModal; 
